/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AddressBookItem } from '@/lib/customer/profile-utils';

export type SavedAddressDropdownProps = {
    addresses: AddressBookItem[];
    value: string;
    onValueChange: (id: string) => void;
    label?: string;
    placeholder?: string;
};

// Single-line summary used both in the closed trigger and in each option row.
export function formatAddressLine(address: AddressBookItem): string {
    return [address.address1, address.address2, address.city, address.stateCode, address.postalCode]
        .filter(Boolean)
        .join(', ');
}

// Desktop option row: "Neha Sharma, A/10 Niketan Apartment, S.V Road, Dadar, Mumbai - 400028"
export function formatAddressOption(address: AddressBookItem): string {
    const name = [address.firstName?.trim(), address.lastName?.trim()].filter(Boolean).join(' ');
    const cityPin = [address.city, address.postalCode].filter(Boolean).join(' - ');
    return [name, address.address1, address.address2, cityPin].filter(Boolean).join(', ');
}

function formatAddressType(type?: string): string | null {
    if (!type) return null;
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
}

/**
 * Saved-address picker for the signed-in checkout flow: a closed input-style trigger that opens a
 * panel of Home/Office-tagged addresses, each with a tick radio on the right.
 */
export function SavedAddressDropdown({
    addresses,
    value,
    onValueChange,
    label = 'Address',
    placeholder = 'Choose Address',
}: SavedAddressDropdownProps): ReactElement {
    const [open, setOpen] = useState(false);
    // With nothing saved, clicking the field shows an error under it instead of an empty panel
    const [showNoAddressesError, setShowNoAddressesError] = useState(false);
    const hasAddresses = addresses.length > 0;
    const rootRef = useRef<HTMLDivElement>(null);
    const listId = useId();
    const selected = addresses.find((a) => a.id === value);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e: PointerEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    return (
        <div ref={rootRef} className="relative w-full">
            {/* Unlike the floating-label fields, the "Address" label always sits on the border;
                "Choose Address" is just the empty-state text inside the box. */}
            <span
                aria-hidden="true"
                className="font-lato pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1 text-xs leading-4 font-normal text-[#555555]">
                {label}
            </span>
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listId}
                aria-label={selected ? `${label}: ${formatAddressLine(selected)}` : label}
                aria-invalid={showNoAddressesError && !hasAddresses}
                aria-describedby={showNoAddressesError && !hasAddresses ? `${listId}-error` : undefined}
                onClick={() => {
                    if (!hasAddresses) {
                        setShowNoAddressesError(true);
                        return;
                    }
                    setOpen((prev) => !prev);
                }}
                className={cn(
                    'font-lato flex h-[62px] w-full items-center gap-3 border bg-white px-3 py-4 min-[787px]:px-[8.5px] min-[787px]:py-5 text-left text-base leading-[22px] text-[#17171B] outline-none',
                    open ? 'rounded-t' : 'rounded',
                    showNoAddressesError && !hasAddresses ? 'border-destructive' : 'border-[#262626]'
                )}>
                <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-[#7B7B7B]')}>
                    {selected ? formatAddressLine(selected) : placeholder}
                </span>
                <ChevronDown className={cn('size-5 shrink-0 transition-transform', open && 'rotate-180')} />
            </button>

            {showNoAddressesError && !hasAddresses && (
                <p id={`${listId}-error`} role="alert" className="text-destructive text-sm mt-1">
                    You don&apos;t have any saved addresses
                </p>
            )}

            {open && (
                <div
                    id={listId}
                    role="listbox"
                    aria-label={label}
                    className="absolute top-full right-0 left-0 z-20 max-h-[242px] overflow-y-auto rounded-b border-x border-b border-t-0 border-solid border-[color:var(--input-selected-active,#262626)] bg-white px-4 py-2.5 sm:max-h-[320px]">
                    <div className="flex flex-col gap-3">
                        {addresses.map((address) => {
                            const isSelected = address.id === value;
                            const typeLabel = formatAddressType(address.addressType);
                            return (
                                <div
                                    key={address.id}
                                    role="option"
                                    aria-selected={isSelected}
                                    tabIndex={0}
                                    onClick={() => {
                                        onValueChange(address.id);
                                        setOpen(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onValueChange(address.id);
                                            setOpen(false);
                                        }
                                    }}
                                    className="flex cursor-pointer flex-col gap-1 outline-none focus-visible:bg-black/[0.04]">
                                    {typeLabel && (
                                        <span className="font-lato inline-flex w-fit items-center justify-center rounded-[4px] bg-[color:var(--card-bg-primary,#E9E9E9)] px-1 py-[6.5px] text-center text-xs leading-[14px] font-normal text-[#17171B] min-[787px]:h-6 min-[787px]:gap-2 min-[787px]:border min-[787px]:border-[color:var(--card-bg-primary,#E9E9E9)] min-[787px]:px-2 min-[787px]:py-1 min-[787px]:leading-4">
                                            {typeLabel}
                                        </span>
                                    )}
                                    <div className="flex items-center gap-4">
                                        <span className="font-lato line-clamp-2 min-w-0 flex-1 text-xs leading-[18px] font-normal text-[#17171B] min-[787px]:hidden">
                                            {formatAddressLine(address)}
                                        </span>
                                        <span className="font-lato line-clamp-2 min-w-0 flex-1 text-sm leading-5 font-normal text-[#17171B] hidden min-[787px]:block">
                                            {formatAddressOption(address)}
                                        </span>
                                        <span
                                            aria-hidden
                                            className={cn(
                                                'flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] p-0.5 min-[787px]:size-6 min-[787px]:p-1',
                                                isSelected ? 'border-black bg-black text-white' : 'border-[#7B7B7B]'
                                            )}>
                                            {isSelected && <Check className="size-full" strokeWidth={3} />}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SavedAddressDropdown;
